// ---------------------------------------------------------------------------
// Replay helpers for "tune one day of the generated week".
//
// Source of truth: the live `CalendarEvent[]` array kept by ModalCalendarContent.
// The user removes proposed visits directly on the calendar (filter them out of
// the array) and drags new companies in. When they hit "Replay day", we build
// a new TSPBody where every KEPT planned TemporaryEvent is locked into place
// (order and slot hold) while dragged-in events stay unlocked mandatory jobs
// the solver sequences freely between the locks. Removed events are simply
// absent from `locked_visits`, leaving the solver free to substitute.
// ---------------------------------------------------------------------------

import type { LockedVisit } from 'bindings/optimization/LockedVisit';
import type { PointConfig } from 'bindings/optimization/PointConfig';
import type { TSPBody } from 'bindings/optimization/TSPBody';

// ---------------------------------------------------------------------------
// Calendar-event → LockedVisit
//
// `points` is the `points[]` array used in the previous TSPBody. We look up the
// node_index by company id. Events whose company has no matching node are
// dropped silently — they can't be locked without an index.
//
// Caller supplies the conversion functions because both helpers
// (`jsDateToNaiveDate`, `secondsSinceMidnight`) already live next to the body
// builder and we don't want to duplicate them.
// ---------------------------------------------------------------------------

export interface EventLike {
	clientCompanyId?: number | null
	start: Date
	end: Date
}

export function eventsToLockedVisits(
	events: ReadonlyArray<EventLike>,
	points: ReadonlyArray<PointConfig>,
	dateToIso: (d: Date) => string,
	secondsSinceMidnight: (d: Date) => number,
	// Default true: kept generated visits may have been placed slightly past
	// working_hours.end by the original solver via overtime. Locking them
	// without overtime would make those edges infeasible and the whole replay
	// would return no solution. The solver only extends the day's horizon
	// when actually required, so leaving this on costs nothing for visits
	// inside normal hours.
	allowOvertime = true,
	// Symmetric slack in seconds on each side of the visit window.
	//
	// Pairs with the new `duration` field on LockedVisit. The window becomes an
	// arrival flexibility range — the solver may start the visit anywhere in
	// the widened window and the visit itself lasts `duration` seconds. This
	// absorbs the minute-rounding mismatch between the solver's "HH:MM" output
	// strings and the sub-minute travel-time matrix, which otherwise causes
	// occasional INFEASIBLE on a replay built from a previous solve.
	//
	// 5 min was chosen because most travel-time round-trip errors are well
	// under that, while still keeping kept visits roughly where the user
	// expects them.
	slackSecs = 300,
): LockedVisit[] {
	const result: LockedVisit[] = [];
	for (const e of events) {
		if (e.clientCompanyId == null) continue;
		const nodeIdx = points.findIndex(p => p.id === e.clientCompanyId);
		if (nodeIdx < 0) continue;
		const rawStart = secondsSinceMidnight(e.start);
		const rawEnd = secondsSinceMidnight(e.end);
		if (rawEnd <= rawStart) continue;
		const duration = rawEnd - rawStart;
		const start = Math.max(0, rawStart - slackSecs);
		const end = Math.min(86400, rawEnd + slackSecs);
		result.push({
			node_index: nodeIdx,
			day: dateToIso(e.start),
			visit_time: { start, end },
			allow_overtime: allowOvertime,
			duration,
		});
	}
	return result;
}

// ---------------------------------------------------------------------------
// Body builder
//
// Combines `baseBody.locked_visits` (the user's original hard pins) with
// generated locks derived from the current calendar state. On duplicate
// (node_index, day), the original wins — its explicit window beats a
// generated tight window.
// ---------------------------------------------------------------------------

export function buildReplayBody(baseBody: TSPBody, derivedLockedVisits: LockedVisit[]): TSPBody {
	const seen = new Set(baseBody.locked_visits.map(lv => `${lv.node_index}@${lv.day}`));
	const merged: LockedVisit[] = [...baseBody.locked_visits];
	for (const lv of derivedLockedVisits) {
		const key = `${lv.node_index}@${lv.day}`;
		if (seen.has(key)) continue;
		seen.add(key);
		merged.push(lv);
	}
	return { ...baseBody, locked_visits: merged };
}
